/**
 * IframeLoader Component
 *
 * Displays a loading spinner overlay while an iframe is loading.
 * Can also show error messages if loading fails.
 *
 * @module minting/components/IframeLoader
 * @category Minting Framework
 */

import React from 'react';

/**
 * Props for the IframeLoader component
 */
export interface IframeLoaderProps {
  /** Whether the iframe is currently loading */
  isLoading: boolean;
  /** Optional message to display below spinner */
  message?: string;
  /** Optional error message to display instead of spinner */
  error?: string | null;
  /** Optional additional CSS class */
  className?: string;
}

/**
 * IframeLoader - A loading overlay for embedded iframes.
 *
 * Shows a centered spinner with optional message. If an error is provided,
 * shows the error instead of the spinner.
 *
 * @example
 * ```tsx
 * <IframeLoader isLoading={loading} message="Loading dapp..." />
 * ```
 */
export const IframeLoader: React.FC<IframeLoaderProps> = ({
  isLoading,
  message = 'Loading...',
  error = null,
  className = '',
}) => {
  // Don't render anything if not loading and no error
  if (!isLoading && !error) {
    return null;
  }

  return (
    <div
      className={`embedded-dapp-loader ${className}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 10,
      }}
      role="status"
      aria-live="polite"
    >
      <div className="text-center">
        {error ? (
          <>
            <i
              className="fas fa-exclamation-triangle fa-3x text-warning mb-3"
              aria-hidden="true"
            ></i>
            <p className="text-light mb-0">{error}</p>
          </>
        ) : (
          <>
            <div
              className="spinner-border text-primary"
              role="progressbar"
              aria-label={message}
            >
              <span className="visually-hidden">{message}</span>
            </div>
            <p className="text-light mt-2 mb-0 small">{message}</p>
          </>
        )}
      </div>
    </div>
  );
};
